import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Package, Eye, Upload, X, FileText, Image as ImageIcon } from 'lucide-react';

const ProductMaster = () => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    specifications: '',
    unit: 'Pcs',
    description: '',
    brand_id: '',
    category_id: '',
    supplier_id: '',
    tax_group_id: '',
    hsn_number: '',
    base_price: '',
    profit_margin: '',
    mrp: ''
  });
  
  // File upload states
  const [imageFiles, setImageFiles] = useState([]);
  const [brochureFile, setBrochureFile] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [existingBrochure, setExistingBrochure] = useState(null);
  const imageInputRef = useRef(null);
  const brochureInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMasterData = useCallback(async () => {
    try {
      const [brandsRes, categoriesRes, suppliersRes, taxGroupsRes, unitsRes] = await Promise.all([
        api.get('/brands/'),
        api.get('/categories/'),
        api.get('/suppliers/'),
        api.get('/taxes/groups'),
        api.get('/products/units')
      ]);
      setBrands(brandsRes.data.filter(b => b.status === 'Active'));
      setCategories(categoriesRes.data.filter(c => c.status === 'Active'));
      setSuppliers(suppliersRes.data.filter(s => s.status === 'Active'));
      setTaxGroups(taxGroupsRes.data.filter(t => t.status === 'Active'));
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Failed to fetch master data');
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchMasterData();
  }, [fetchProducts, fetchMasterData]);

  const handleOpenDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name,
        model: product.model || '',
        specifications: product.specifications || '',
        unit: product.unit || 'Pcs',
        description: product.description || '',
        brand_id: product.brand_id || '',
        category_id: product.category_id || '',
        supplier_id: product.supplier_id || '',
        tax_group_id: product.tax_group_id || '',
        hsn_number: product.hsn_number || '',
        base_price: product.base_price || '',
        profit_margin: product.profit_margin || '',
        mrp: product.mrp || ''
      });
      setExistingImages(product.images || []);
      setExistingBrochure(product.brochure || null);
    } else {
      setSelectedProduct(null);
      setFormData({
        name: '',
        model: '',
        specifications: '',
        unit: 'Pcs',
        description: '',
        brand_id: '',
        category_id: '',
        supplier_id: '',
        tax_group_id: '',
        hsn_number: '',
        base_price: '',
        profit_margin: '',
        mrp: ''
      });
      setExistingImages([]);
      setExistingBrochure(null);
    }
    setImageFiles([]);
    setBrochureFile(null);
    setDialogOpen(true);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setViewDialogOpen(true);
  };

  // Image file handling
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    for (const file of files) {
      // Check file type
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error(`${file.name}: Only JPG and PNG images are allowed`);
        continue;
      }
      // Check file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name}: File exceeds 2MB limit`);
        continue;
      }
      validFiles.push(file);
    }
    
    setImageFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeImageFile = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Brochure file handling
  const handleBrochureSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed for brochure');
      e.target.value = '';
      return;
    }
    // Check file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Brochure file exceeds 5MB limit');
      e.target.value = '';
      return;
    }
    
    setBrochureFile(file);
    e.target.value = '';
  };

  const removeBrochureFile = () => {
    setBrochureFile(null);
  };

  // Delete existing image
  const handleDeleteExistingImage = async (index) => {
    if (!selectedProduct) return;
    
    try {
      await api.delete(`/products/${selectedProduct.id}/images/${index}`);
      setExistingImages(prev => prev.filter((_, i) => i !== index));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  // Delete existing brochure
  const handleDeleteExistingBrochure = async () => {
    if (!selectedProduct) return;
    
    try {
      await api.delete(`/products/${selectedProduct.id}/brochure`);
      setExistingBrochure(null);
      toast.success('Brochure deleted');
    } catch (error) {
      toast.error('Failed to delete brochure');
    }
  };

  const uploadFiles = async (productId) => {
    // Upload images
    if (imageFiles.length > 0) {
      const formData = new FormData();
      imageFiles.forEach(file => {
        formData.append('files', file);
      });
      
      try {
        await api.post(`/products/${productId}/upload-images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        toast.error('Failed to upload images');
      }
    }
    
    // Upload brochure
    if (brochureFile) {
      const formData = new FormData();
      formData.append('file', brochureFile);
      
      try {
        await api.post(`/products/${productId}/upload-brochure`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        toast.error('Failed to upload brochure');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    setUploading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      let productId;
      if (selectedProduct) {
        await api.put(`/products/${selectedProduct.id}?${params.toString()}`);
        productId = selectedProduct.id;
        toast.success('Product updated successfully');
      } else {
        const response = await api.post(`/products/?${params.toString()}`);
        productId = response.data.product.id;
        toast.success('Product created successfully');
      }
      
      // Upload files if any
      if (imageFiles.length > 0 || brochureFile) {
        await uploadFiles(productId);
      }
      
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"?`)) return;

    try {
      await api.delete(`/products/${product.id}`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.product_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.hsn_number?.includes(searchTerm)
  );

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate selling price based on base price, profit margin, and tax
  const calculateSellingPrice = () => {
    const basePrice = parseFloat(formData.base_price) || 0;
    const profitMargin = parseFloat(formData.profit_margin) || 0;
    
    // SP before tax = Base Price × (1 + Profit Margin%)
    const spBeforeTax = basePrice * (1 + profitMargin / 100);
    
    // Get tax percentage from selected tax group
    const selectedTaxGroup = taxGroups.find(t => t.id === formData.tax_group_id);
    const taxPercentage = selectedTaxGroup?.total_percentage || 0;
    
    // Tax amount = SP before tax × Tax%
    const taxAmount = spBeforeTax * (taxPercentage / 100);
    
    // Final Selling Price = SP before tax + Tax
    const sellingPrice = spBeforeTax + taxAmount;
    
    return {
      spBeforeTax: spBeforeTax.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      sellingPrice: sellingPrice.toFixed(2),
      taxPercentage
    };
  };

  const priceCalc = calculateSellingPrice();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Master</h1>
          <p className="text-gray-600">Manage products and inventory items</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Products ({filteredProducts.length})
          </CardTitle>
          <div className="flex items-center gap-2 mt-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Base Price</TableHead>
                <TableHead className="text-right">Selling Price</TableHead>
                <TableHead className="text-right">MRP</TableHead>
                <TableHead>Files</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-mono text-sm">{product.product_id}</TableCell>
                    <TableCell>
                      <div>
                        <span className="font-medium">{product.name}</span>
                        {product.model && <span className="text-sm text-gray-500 ml-1">({product.model})</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{product.brand_name || '-'}</Badge>
                    </TableCell>
                    <TableCell>{product.category_name || '-'}</TableCell>
                    <TableCell className="text-right">₹{(product.base_price || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right font-medium text-green-700">₹{(product.selling_price || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right">₹{(product.mrp || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {product.images?.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            <ImageIcon className="h-3 w-3 mr-1" />
                            {product.images.length}
                          </Badge>
                        )}
                        {product.brochure && (
                          <Badge variant="secondary" className="text-xs">
                            <FileText className="h-3 w-3 mr-1" />
                            PDF
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleViewProduct(product)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(product)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(product)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-gray-500 py-8">
                    No products found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter product name"
                  />
                </div>
                <div>
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Enter model number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="brand_id">Brand</Label>
                  <Select
                    value={formData.brand_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, brand_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Brand</SelectItem>
                      {brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="category_id">Category</Label>
                  <Select
                    value={formData.category_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.parent_name !== 'Root' ? `${category.parent_name} > ` : ''}{category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="supplier_id">Supplier</Label>
                  <Select
                    value={formData.supplier_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, supplier_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Supplier</SelectItem>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="unit">Unit *</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="tax_group_id">Tax Group</Label>
                  <Select
                    value={formData.tax_group_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, tax_group_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tax group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Tax</SelectItem>
                      {taxGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name} ({group.total_percentage}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="hsn_number">HSN Number</Label>
                  <Input
                    id="hsn_number"
                    value={formData.hsn_number}
                    onChange={(e) => setFormData({ ...formData, hsn_number: e.target.value })}
                    placeholder="Enter HSN code"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="specifications">Specifications</Label>
                <Textarea
                  id="specifications"
                  value={formData.specifications}
                  onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                  placeholder="Enter product specifications"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter product description"
                  rows={2}
                />
              </div>

              {/* Pricing Section */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <Label className="text-base font-medium text-blue-800">Pricing (GST Compliant)</Label>
                <p className="text-sm text-blue-600 mb-3">Selling Price = Base Price × (1 + Profit Margin%) + GST</p>
                
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="base_price">Base Price (₹)</Label>
                    <Input
                      id="base_price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.base_price}
                      onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profit_margin">Profit Margin (%)</Label>
                    <Input
                      id="profit_margin"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.profit_margin}
                      onChange={(e) => setFormData({ ...formData, profit_margin: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Selling Price (₹)</Label>
                    <div className="h-10 px-3 py-2 bg-green-100 border border-green-300 rounded-md text-green-800 font-semibold">
                      ₹{priceCalc.sellingPrice}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Before Tax: ₹{priceCalc.spBeforeTax} + GST ({priceCalc.taxPercentage}%): ₹{priceCalc.taxAmount}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="mrp">MRP (₹)</Label>
                    <Input
                      id="mrp"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.mrp}
                      onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Must be ≥ Selling Price</p>
                  </div>
                </div>
              </div>

              {/* Product Images Upload */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-medium">Product Images (Optional)</Label>
                <p className="text-sm text-gray-500 mb-3">Upload product images. JPG/PNG only, max 2MB each.</p>
                
                <input
                  ref={imageInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => imageInputRef.current?.click()}
                  className="mb-3"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Select Images
                </Button>

                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-600 mb-2">Existing Images:</p>
                    <div className="grid grid-cols-4 gap-2">
                      {existingImages.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={`${backendUrl}${img}`}
                            alt={`Product ${index + 1}`}
                            className="w-full h-24 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingImage(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Images to Upload */}
                {imageFiles.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">New Images to Upload:</p>
                    <div className="space-y-2">
                      {imageFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                          <div className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4 text-blue-500" />
                            <span className="text-sm truncate max-w-xs">{file.name}</span>
                            <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeImageFile(index)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <X className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Product Brochure Upload */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-medium">Product Brochure (Optional)</Label>
                <p className="text-sm text-gray-500 mb-3">Upload product brochure. PDF only, max 5MB.</p>
                
                <input
                  ref={brochureInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleBrochureSelect}
                  className="hidden"
                />
                
                {!existingBrochure && !brochureFile && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => brochureInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Select PDF Brochure
                  </Button>
                )}

                {/* Existing Brochure */}
                {existingBrochure && (
                  <div className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-red-500" />
                      <span className="text-sm">Existing Brochure</span>
                      <a
                        href={`${backendUrl}${existingBrochure}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm hover:underline"
                      >
                        View PDF
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={handleDeleteExistingBrochure}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                )}

                {/* New Brochure to Upload */}
                {brochureFile && (
                  <div className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-red-500" />
                      <span className="text-sm truncate max-w-xs">{brochureFile.name}</span>
                      <span className="text-xs text-gray-400">({formatFileSize(brochureFile.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeBrochureFile}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? 'Saving...' : (selectedProduct ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Product Details</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Product ID</Label>
                  <p className="font-mono font-medium">{selectedProduct.product_id}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Name</Label>
                  <p className="font-medium">{selectedProduct.name}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Model</Label>
                  <p className="font-medium">{selectedProduct.model || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Unit</Label>
                  <p className="font-medium">{selectedProduct.unit}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Brand</Label>
                  <p className="font-medium">{selectedProduct.brand_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Category</Label>
                  <p className="font-medium">{selectedProduct.category_name || '-'}</p>
                </div>
              </div>
              
              {/* Pricing Section */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <Label className="text-blue-800 font-medium">Pricing</Label>
                <div className="grid grid-cols-4 gap-4 mt-2">
                  <div>
                    <Label className="text-gray-500 text-xs">Base Price</Label>
                    <p className="font-medium">₹{(selectedProduct.base_price || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Profit Margin</Label>
                    <p className="font-medium">{selectedProduct.profit_margin || 0}%</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Selling Price</Label>
                    <p className="font-semibold text-green-700">₹{(selectedProduct.selling_price || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">MRP</Label>
                    <p className="font-medium">₹{(selectedProduct.mrp || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Supplier</Label>
                  <p className="font-medium">{selectedProduct.supplier_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Tax Group</Label>
                  <p className="font-medium">{selectedProduct.tax_group_name || '-'}</p>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">HSN Number</Label>
                <p className="font-medium">{selectedProduct.hsn_number || '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Specifications</Label>
                <p className="font-medium">{selectedProduct.specifications || '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Description</Label>
                <p className="font-medium">{selectedProduct.description || '-'}</p>
              </div>

              {/* Product Images */}
              {selectedProduct.images?.length > 0 && (
                <div>
                  <Label className="text-gray-500">Product Images</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {selectedProduct.images.map((img, index) => (
                      <a
                        key={index}
                        href={`${backendUrl}${img}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={`${backendUrl}${img}`}
                          alt={`Product ${index + 1}`}
                          className="w-full h-24 object-cover rounded border hover:opacity-80 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Brochure */}
              {selectedProduct.brochure && (
                <div>
                  <Label className="text-gray-500">Product Brochure</Label>
                  <div className="mt-2">
                    <a
                      href={`${backendUrl}${selectedProduct.brochure}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <FileText className="h-5 w-5" />
                      View Brochure (PDF)
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductMaster;
